import type React from 'react';

import { cn } from '@change/ds-utils';

import type { MediaHitEmbed } from '../../shared/context';
import { useMediaHitsContext } from '../../shared/context';
import { useMediaHitsPermissions } from '../../shared/hooks/useMediaHitsPermissions';

import { MediaHitItem } from './components/MediaHitItem';

export const MAX_ITEMS = 3;

/**
 * Valid hits are prioritized ahead of invalid ones (staff-only, kept so they can be deleted rather
 * than collapsing to a gap) so a run of invalid hits can never crowd real content out of the fixed
 * `MAX_ITEMS` slots. Exported so `useMediaHits` can size "see all" against the same visible set —
 * otherwise the two could drift and "see all" would show up pointing at nothing new.
 */
export function visibleMediaHitEmbeds(
	mediaEmbeds: readonly MediaHitEmbed[],
	canDelete: boolean,
): readonly MediaHitEmbed[] {
	const validEmbeds = mediaEmbeds.filter((embed) => embed.status !== 'invalid');
	const invalidEmbeds = canDelete ? mediaEmbeds.filter((embed) => embed.status === 'invalid') : [];
	return [...validEmbeds, ...invalidEmbeds];
}

/**
 * Treatment-arm body of the media mentions section: Change-owned press clippings in place of the
 * third-party iframely embeds.
 */
export const MediaHitGrid: React.FC<EmptyProps> = () => {
	const {
		data: { mediaEmbeds },
		actions: { removeMedia },
	} = useMediaHitsContext();
	const {
		data: { canDelete },
	} = useMediaHitsPermissions();

	const visibleEmbeds = visibleMediaHitEmbeds(mediaEmbeds, canDelete).slice(0, MAX_ITEMS);

	const [lead, ...secondaries] = visibleEmbeds;

	if (!lead) return null;
	const hasSecondaries = secondaries.length > 0;

	return (
		<div data-testid="media-hit-grid" className="mt-5 md:grid md:grid-cols-[1.35fr_1fr]">
			<div className={cn({ 'md:border-stroke-weaker md:border-r md:pr-7': hasSecondaries })}>
				<MediaHitItem media={lead} removeMedia={removeMedia} featured />
			</div>
			{hasSecondaries && (
				<div className="flex flex-col md:pl-7">
					{secondaries.map((media, index) => (
						<div
							key={media.id}
							className={cn(
								'border-stroke-weaker mt-5 border-t pt-5',
								// The first secondary sits at the top of the right-hand cell on desktop, so its rule
								// would double up with the lead item's vertical divider.
								index === 0 && 'md:mt-0 md:border-t-0 md:pt-0',
							)}
						>
							<MediaHitItem media={media} removeMedia={removeMedia} />
						</div>
					))}
				</div>
			)}
		</div>
	);
};
